import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import React, { useEffect, useState, useRef } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, ListItemIcon, ListItemText, Menu, IconButton } from '@mui/material';

import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsIcon from "@mui/icons-material/Settings";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import './SalesAccounts.css';
import SignatureModal from '../../components/Signature/SignatureModal';
import GmailConnectModal from '../../components/Integration/GmailConnectModal';
import EmailSettingsModal from '../../components/Integration/EmailSettingsModal';
import TableAnimationFiles from '../../components/Animations/TableAnimationFiles';
import { getSalesAccounts, createSalesAccount, updateSalesAccount, deleteSalesAccount, getIntegrations, createIntegration, linkIntegrationToSalesAccount, unlinkIntegrationFromSalesAccount, getEmailSettings, getDomainHealth } from '../../store/Integration';

// Role options for the dropdown
const ROLE_OPTIONS = [
    'Chief Executive Officer',
    'Director',
    'Software Engineer',
    'Consultant',
    'Account Manager',
    'Engineer',
    'Professor',
    'Sales Manager',
    'Sales',
    'Partner',
    'Marketing Manager',
    'Business Development',
    'Customer Success',
    'Support'
];

// Integration type configurations
const INTEGRATION_CONFIG = {
    email: {
        name: 'Gmail',
        title: 'Gmail',
        toolName: 'Gmail',
        description: 'Connect your Gmail accounts',
        icon: (
            <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.6632 7.07544L10.1556 8.58807C9.5443 9.20155 8.46866 9.21477 7.8441 8.58807L6.3365 7.07544L0.921875 12.5076C1.12343 12.6008 1.34565 12.6565 1.5819 12.6565H16.4178C16.6541 12.6565 16.8762 12.6009 17.0777 12.5077L11.6632 7.07544Z" fill="#120348" />
                <path d="M16.418 0H1.58203C1.34578 0 1.12356 0.0556172 0.922078 0.148816L6.70799 5.95392C6.70837 5.95431 6.70883 5.95438 6.70922 5.95477C6.70946 5.95502 6.70962 5.95534 6.70968 5.95568L8.59099 7.84322C8.79082 8.04305 9.20925 8.04305 9.40908 7.84322L11.29 5.956C11.29 5.956 11.2905 5.95515 11.2909 5.95477C11.2909 5.95477 11.2917 5.95431 11.2921 5.95392L17.0779 0.148781C16.8764 0.055547 16.6542 0 16.418 0ZM0.168258 0.886359C0.0639844 1.09723 0 1.3313 0 1.58203V11.0742C0 11.325 0.0639141 11.559 0.168223 11.7699L5.59223 6.3283L0.168258 0.886359ZM17.8317 0.886289L12.4078 6.3283L17.8317 11.77C17.936 11.5591 18 11.325 18 11.0742V1.58203C18 1.33123 17.936 1.09716 17.8317 0.886289Z" fill="#120348" />
            </svg>
        ),
    },
    sms: {
        toolName: 'Twilio',
        name: 'SMS (Twilio)',
        title: 'SMS (Twilio)',
        description: 'Connect your Twilio accounts',
        icon: (
            <img src={require('../../static/img/chatting.png')} alt='' />
        ),
    },
    whatsapp: {
        name: 'WhatsApp',
        title: 'WhatsApp',
        toolName: 'WhatsApp',
        description: 'Connect your WhatsApp accounts',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="#25D366" />
            </svg>
        ),
    }
}

const SalesAccounts = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();

    const [editingField, setEditingField] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
    const [formData, setFormData] = useState({ name: '', role: '' });
    const [actionsIntegration, setActionsIntegration] = useState(null);

    const openActionsMenu = Boolean(actionsAnchorEl);

    const handleOpenActionsMenu = (e, integration) => {
        e.stopPropagation();
        setActionsAnchorEl(e.currentTarget);
        setActionsIntegration(integration);
    };

    const handleCloseActionsMenu = () => {
        setActionsAnchorEl(null);
        setActionsIntegration(null);
    };

    // Collapsible state for each channel - all expanded by default
    const [expandedChannels, setExpandedChannels] = useState({ sms: true, email: true, whatsapp: true });

    // Connect integration popover state
    const popoverRef = useRef(null);
    const popoverCloseTimerRef = useRef(null);
    const [connectPopover, setConnectPopover] = useState({ open: false, channel: null });
    const [gmailConnectOpen, setGmailConnectOpen] = useState(false);

    // Email settings modal state
    const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
    const [selectedIntegrationForSettings, setSelectedIntegrationForSettings] = useState(null);
    const [checkedIntegrations, setCheckedIntegrations] = useState(new Set());
    const [domainHealthMap, setDomainHealthMap] = useState({});
    const [domainHealthLoading, setDomainHealthLoading] = useState({});
    const [domainCheckStatus, setDomainCheckStatus] = useState(null);
    const [healthTooltipId, setHealthTooltipId] = useState(null);

    const toggleIntegrationCheck = (id) => setCheckedIntegrations(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // Signature modal state
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [savingSignature, setSavingSignature] = useState(false);

    const integrationState = useSelector(state => state['Integration'] || {});
    const { currentWorkspace } = useSelector(state => state['Settings'] || {});

    // Safe access to state with defaults
    const integrations = integrationState['integrations'] || [];
    const salesAccounts = integrationState['salesAccounts'] || { loading: false, data: [], error: null };
    const salesAccountForm = integrationState['salesAccountForm'] || { loading: false, success: false, error: null };
    const emailSettings = integrationState['emailSettings'] || { data: {} };

    const userRole = currentWorkspace?.['role'] || 'view';
    const canManage = ['owner', 'admin', 'editor'].includes(userRole);

    useEffect(() => {
        dispatch(getSalesAccounts());
        dispatch(getIntegrations());
    }, [dispatch]);

    // Auto-select account from URL param or default to first
    useEffect(() => {
        if (salesAccounts['data'].length === 0) return;
        const urlParams = new URLSearchParams(location.search);
        const accountId = urlParams.get('account');
        if (accountId) {
            const account = salesAccounts['data'].find(a => a._id === accountId);
            if (account) { setSelectedAccount(account); return; }
        }
        if (!selectedAccount) setSelectedAccount(salesAccounts['data'][0]);
    }, [salesAccounts['data']]);

    // Close modal on successful create
    useEffect(() => {
        if (salesAccountForm['success'] && showCreateModal) {
            setShowCreateModal(false);
            resetForm();
        }
    }, [salesAccountForm['success']]);

    // Update selectedAccount when salesAccounts data changes
    useEffect(() => {
        if (selectedAccount) {
            const updated = salesAccounts.data.find(acc => acc._id === selectedAccount._id);
            if (updated) setSelectedAccount(updated);
        }
    }, [salesAccounts.data]);

    // Fetch email settings for all linked email integrations
    useEffect(() => {
        if (selectedAccount?.integrations) {
            const emailIntegrations = selectedAccount.integrations.filter(i => i?.toolId?.name === 'Gmail');
            emailIntegrations.forEach(integration => {
                if (!emailSettings.data[integration._id]) dispatch(getEmailSettings(integration._id));
            });
        }
    }, [selectedAccount?.integrations, dispatch]);

    const resetForm = () => setFormData({ name: '', role: '' });

    const handleOpenCreate = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        resetForm();
    };

    const handleCreateSubmit = (e) => {
        e.preventDefault();
        dispatch(createSalesAccount({ name: formData.name, role: formData.role }));
    };

    const handleSelectAccount = (account) => {
        setSelectedAccount(account);
        const params = new URLSearchParams(location.search);
        params.set('account', account._id);
        history.replace({ search: params.toString() });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this sales account?')) {
            dispatch(deleteSalesAccount(id));
            setSelectedAccount(null);
        }
    };

    const handleUpdateRole = (newRole) => {
        if (!selectedAccount) return;
        dispatch(updateSalesAccount(selectedAccount._id, { role: newRole }));
    };

    const getIntegrationLabel = (integration) => {
        if (!integration) return '';
        return integration.tokens?.email || integration.accountIdentifier || integration.publicKey || integration.whatsappAccount?.phoneNumber || integration.name;
    };

    // Get integrations by channel type from the global integrations list
    const getAvailableIntegrationsByChannel = (channel) => {
        const toolName = INTEGRATION_CONFIG[channel]?.toolName;
        return integrations.filter(i => i?.toolId?.name === toolName);
    };

    // Get integrations linked to the current sales account by channel type
    const getLinkedIntegrationsByChannel = (channel) => {
        if (!selectedAccount?.integrations) return [];
        const toolName = INTEGRATION_CONFIG[channel]?.toolName;
        return selectedAccount.integrations.filter(i => i?.toolId?.name === toolName);
    };

    // Toggle channel expansion
    const toggleChannel = (channel) => {
        setExpandedChannels(prev => ({ ...prev, [channel]: !prev[channel] }));
    };

    // Handle opening the connect popover
    const handleOpenConnectPopover = (channel) => setConnectPopover({ open: true, channel });

    // Handle closing the connect popover
    const handleCloseConnectPopover = () => setConnectPopover({ open: false, channel: null });

    // Hover-open with delayed close so the cursor can move from button to popover without dismissing it
    const handleConnectMouseEnter = (channel) => {
        if (popoverCloseTimerRef['current']) { clearTimeout(popoverCloseTimerRef['current']); popoverCloseTimerRef['current'] = null; }
        handleOpenConnectPopover(channel);
    };

    const handleConnectMouseLeave = () => {
        popoverCloseTimerRef['current'] = setTimeout(() => handleCloseConnectPopover(), 200);
    };

    // Handle linking an integration to sales account
    const handleLinkIntegration = (integrationId) => {
        if (!selectedAccount) return;
        dispatch(linkIntegrationToSalesAccount(selectedAccount._id, integrationId));
        handleCloseConnectPopover();
    };

    // Handle unlinking an integration from sales account
    const handleUnlinkIntegration = (integrationId) => dispatch(unlinkIntegrationFromSalesAccount(integrationId));

    // Handle connecting a new integration account directly
    const handleConnectNewAccount = (channel) => {
        const config = INTEGRATION_CONFIG[channel];
        if (!config || !selectedAccount) return;

        // For Gmail - open the unified connect modal (OAuth or App Password)
        if (channel === 'email') {
            handleCloseConnectPopover();
            setGmailConnectOpen(true);
        } else {
            // For API key channels (Twilio, WhatsApp), navigate to global integrations page
            history.push('/connections/integrations');
        }
    };

    const handleToggleWarmup = (integration) => {
        const ws = emailSettings.data[integration._id]?.warmup || {};
        const isActive = ws.enabled && !ws.paused;
        const updatedWarmup = isActive ? { ...ws, enabled: true, paused: true } : { ...ws, enabled: true, paused: false };  // disabled/paused → active
        dispatch({ type: 'UPDATE_EMAIL_SETTINGS', payload: { integrationId: integration._id, settings: { warmup: updatedWarmup } } });
    };

    const handleCheckDomainHealth = (integrationIds) => {
        if (!integrationIds.length) return;
        setDomainCheckStatus('checking');
        setDomainHealthLoading(Object.fromEntries(integrationIds.map(id => [id, true])));
        let remaining = integrationIds.length;
        integrationIds.forEach(id => {
            dispatch(getDomainHealth(id, (result) => {
                setDomainHealthMap(prev => ({ ...prev, [id]: result }));
                setDomainHealthLoading(prev => ({ ...prev, [id]: false }));
                remaining -= 1;
                if (remaining === 0) setDomainCheckStatus('done');
            }));
        });
    };

    // Get email stats for an integration
    const getEmailStats = (integrationId) => {
        const settings = emailSettings.data[integrationId];
        if (!settings) return { warmupEmails: 0, emailsSent: 0, healthScore: 0 };
        const stats = settings.stats || {};
        const sent = stats.sent || 0;
        const received = stats.received || 0;
        const savedFromSpam = stats.savedFromSpam || 0;
        // Health = inbox delivery rate: penalize emails rescued from spam out of total received
        // If no spam rescues, health is 100%. More spam rescues = lower health.
        const healthScore = received > 0 ? Math.min(100, Math.round(((received - savedFromSpam) / received) * 100)) : (sent > 0 ? 100 : null);
        return { warmupEmails: received, emailsSent: sent, healthScore };
    };

    // Get health score color
    const getHealthScoreColor = (score) => {
        if (score >= 80) return '#10B981';
        if (score >= 50) return '#F59E0B';
        return '#EF4444';
    };

    // Handle opening email settings modal
    const handleOpenEmailSettings = (integration) => {
        setEmailSettingsOpen(true);
        setSelectedIntegrationForSettings(integration);
    };

    // Handle closing email settings modal
    const handleCloseEmailSettings = () => {
        setEmailSettingsOpen(false);
        setSelectedIntegrationForSettings(null);
    };

    // Handle opening signature modal
    const handleOpenSignatureModal = () => setSignatureModalOpen(true);

    // Handle closing signature modal
    const handleCloseSignatureModal = () => setSignatureModalOpen(false);

    // Handle saving signature
    const handleSaveSignature = ({ content, firstName, lastName, selectedAccounts }) => {
        if (!selectedAccount) return;
        setSavingSignature(true);

        // Extract integration IDs from composite "salesAccountId__integrationId" values
        const integrationIds = (selectedAccounts || []).map(a => {
            const parts = a.value.split('__');
            return parts.length === 2 ? parts[1] : null;
        }).filter(Boolean);

        // Update sender on all selected integrations in parallel (single dispatch)
        if (integrationIds.length > 0) dispatch({ type: 'UPDATE_EMAIL_SETTINGS_BULK', payload: { integrationIds, settings: { sender: { firstName, lastName, signature: content } } } });
        
        dispatch(updateSalesAccount(selectedAccount._id, {
            signature: content,
            onSuccess: () => { setSavingSignature(false); handleCloseSignatureModal(); },
            onFailure: () => setSavingSignature(false)
        }));
    };

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef['current'] && !popoverRef['current'].contains(event['target'])) handleCloseConnectPopover();
        };

        if (connectPopover['open']) document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [connectPopover['open']]);

    // Clear pending hover-close timer on unmount
    useEffect(() => () => {
        if (popoverCloseTimerRef['current']) clearTimeout(popoverCloseTimerRef['current']);
    }, []);

    if (salesAccounts['loading']) {
        return (
            <div className='integrations-tab sales-accounts-page'>
                <div className='sa-loading-container'>
                    <TableAnimationFiles />
                </div>
            </div>
        )
    }

    const updateAccountName = (id, value) => {
        setSelectedAccount((prev) => ({
            ...prev,
            name: value
        }));
    };

    return (
        <div className='integrations-tab sales-accounts-page'>
            <div className='sa-main-layout'>
                {/* Left Sidebar - Sales Accounts List */}
                <div className='sa-sidebar'>
                    <h3 className='sa-sidebar-title'>Sales Accounts</h3>

                    <div className='sa-accounts-list'>
                        {salesAccounts.data.map(account => (
                            <div
                                key={account._id}
                                onClick={() => handleSelectAccount(account)}
                                className={`sa-account-item ${selectedAccount?._id === account._id ? 'active' : ''}`}
                            >
                                <div className='sa-account-avatar'>
                                    {account.name?.charAt(0)?.toUpperCase() || 'S'}
                                </div>
                                <span className='sa-account-name'>{account.name}</span>
                            </div>
                        ))}
                    </div>

                    {canManage && (
                        <div onClick={handleOpenCreate} className='sa-add-account-btn'>
                            <span>+</span> Add Sales Account
                        </div>
                    )}
                </div>

                {/* Right Content - Selected Sales Account Details */}
                <div className='sa-content'>
                    {selectedAccount ? (
                        <>
                            {/* Header */}
                            <div className='sa-header'>
                                <div className='sa-header-left'>
                                    <div className='sa-header-avatar'>
                                        <i className='fa fa-user'></i>
                                    </div>
                                    <div>
                                        <div className="sa-header-wrapper">
                                            {editingField === "accountName" ? (
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    className="sa-header-name"
                                                    value={selectedAccount?.name || ""}
                                                    onChange={(e) => updateAccountName(selectedAccount?.id, e.target.value)}
                                                    onBlur={() => setEditingField(null)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <h2
                                                    className="sa-header-name"
                                                    onClick={() => setEditingField("accountName")}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    {selectedAccount?.name}
                                                    <i className='icon'>
                                                        <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill="none"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracurrentColorerCarrier" stroke-linecurrentcap="round" stroke-linejoin="round"></g><g id="SVGRepo_icurrentColoronCarrier"> <path stroke="currentColor" stroke-linecurrentcap="round" stroke-linejoin="round" stroke-width="2" d="M3.8 12.963L2 18l4.8-.63L18.11 6.58a2.612 2.612 0 00-3.601-3.785L3.8 12.963z"></path> </g></svg>
                                                    </i>
                                                </h2>
                                            )}
                                        </div>
                                        {/* <h2 className='sa-header-name'>{selectedAccount.name}</h2> */}
                                        <FormControl size="small" className='sa-role-select'>
                                            <Select
                                                value={selectedAccount.role || ''}
                                                onChange={(e) => handleUpdateRole(e.target.value)}
                                                displayEmpty
                                                variant="standard"
                                                className='sa-role-dropdown'
                                            >
                                                <MenuItem value="" className='sa-menu-placeholder'>Select a role...</MenuItem>
                                                {ROLE_OPTIONS.map(role => (
                                                    <MenuItem key={role} value={role}>{role}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>
                                <div className='sa-header-actions'>
                                    {canManage && (
                                        <button className='sa-signature-btn' onClick={handleOpenSignatureModal}>
                                            <i className='icon'>
                                                <svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15.3665 4.01739L11.3492 0L9.19528 2.1539L5.3791 4.42421L3.40006 11.407L3.08141 11.7257C2.8405 11.9666 2.5202 12.0993 2.1795 12.0993C1.83879 12.0993 1.5185 11.9666 1.27755 11.7257C0.794108 11.2422 0.77667 10.4681 1.23788 9.96334L1.84657 9.29711C2.63344 8.43593 2.60373 7.11519 1.77883 6.29032L0.636901 5.14839L0 5.78532L1.14193 6.92725C1.62537 7.41069 1.64281 8.18481 1.1816 8.68957L0.572914 9.35579C-0.213961 10.217 -0.184248 11.5377 0.640653 12.3626C1.05168 12.7736 1.59818 13 2.1795 13C2.76081 13 3.30729 12.7736 3.71831 12.3626L4.03561 12.0453L11.092 10.011L13.2178 6.16615L15.3665 4.01739ZM11.3492 1.27386L14.0927 4.01739L12.8544 5.25572L10.1109 2.51216L11.3492 1.27386ZM10.4736 9.26788L5.29896 10.7876L7.91788 8.17179L8.83636 8.16798L8.81868 6.6146L7.27179 6.63225L7.27564 7.54026L4.6756 10.1372L6.13056 5.02528L9.40315 3.07835L12.2962 5.97143L10.4736 9.26788Z" fill="#120348" />
                                                </svg>
                                            </i>
                                            {selectedAccount.signature ? 'Edit Signature' : 'Add Signature'}
                                        </button>
                                    )}
                                    {canManage && (
                                        <button className='sa-signature-btn' onClick={() => handleDelete(selectedAccount._id)}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#EF4D5600"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M10.3094 2.25002H13.6908C13.9072 2.24988 14.0957 2.24976 14.2737 2.27819C14.977 2.39049 15.5856 2.82915 15.9146 3.46084C15.9978 3.62073 16.0573 3.79961 16.1256 4.00494L16.2373 4.33984C16.2562 4.39653 16.2616 4.41258 16.2661 4.42522C16.4413 4.90933 16.8953 5.23659 17.4099 5.24964C17.4235 5.24998 17.44 5.25004 17.5001 5.25004H20.5001C20.9143 5.25004 21.2501 5.58582 21.2501 6.00004C21.2501 6.41425 20.9143 6.75004 20.5001 6.75004H3.5C3.08579 6.75004 2.75 6.41425 2.75 6.00004C2.75 5.58582 3.08579 5.25004 3.5 5.25004H6.50008C6.56013 5.25004 6.5767 5.24998 6.59023 5.24964C7.10488 5.23659 7.55891 4.90936 7.73402 4.42524C7.73863 4.41251 7.74392 4.39681 7.76291 4.33984L7.87452 4.00496C7.94281 3.79964 8.00233 3.62073 8.08559 3.46084C8.41453 2.82915 9.02313 2.39049 9.72643 2.27819C9.90445 2.24976 10.093 2.24988 10.3094 2.25002ZM9.00815 5.25004C9.05966 5.14902 9.10531 5.04404 9.14458 4.93548C9.1565 4.90251 9.1682 4.86742 9.18322 4.82234L9.28302 4.52292C9.37419 4.24941 9.39519 4.19363 9.41601 4.15364C9.52566 3.94307 9.72853 3.79686 9.96296 3.75942C10.0075 3.75231 10.067 3.75004 10.3553 3.75004H13.6448C13.9331 3.75004 13.9927 3.75231 14.0372 3.75942C14.2716 3.79686 14.4745 3.94307 14.5842 4.15364C14.605 4.19363 14.626 4.2494 14.7171 4.52292L14.8169 4.82216L14.8556 4.9355C14.8949 5.04405 14.9405 5.14902 14.992 5.25004H9.00815Z" fill="#EF4D56"></path> <path d="M5.91509 8.45015C5.88754 8.03685 5.53016 7.72415 5.11686 7.7517C4.70357 7.77925 4.39086 8.13663 4.41841 8.54993L4.88186 15.5017C4.96736 16.7844 5.03642 17.8205 5.19839 18.6336C5.36679 19.4789 5.65321 20.185 6.2448 20.7385C6.8364 21.2919 7.55995 21.5308 8.4146 21.6425C9.23662 21.7501 10.275 21.7501 11.5606 21.75H12.4395C13.7251 21.7501 14.7635 21.7501 15.5856 21.6425C16.4402 21.5308 17.1638 21.2919 17.7554 20.7385C18.347 20.185 18.6334 19.4789 18.8018 18.6336C18.9638 17.8206 19.0328 16.7844 19.1183 15.5017L19.5818 8.54993C19.6093 8.13663 19.2966 7.77925 18.8833 7.7517C18.47 7.72415 18.1126 8.03685 18.0851 8.45015L17.6251 15.3493C17.5353 16.6971 17.4713 17.6349 17.3307 18.3406C17.1943 19.025 17.004 19.3873 16.7306 19.6431C16.4572 19.8989 16.083 20.0647 15.391 20.1552C14.6776 20.2485 13.7376 20.25 12.3868 20.25H11.6134C10.2626 20.25 9.32255 20.2485 8.60915 20.1552C7.91715 20.0647 7.54299 19.8989 7.26958 19.6431C6.99617 19.3873 6.80583 19.025 6.66948 18.3406C6.52892 17.6349 6.46489 16.6971 6.37503 15.3493L5.91509 8.45015Z" fill="#EF4D56"></path> <path d="M9.42546 10.2538C9.83762 10.2125 10.2052 10.5133 10.2464 10.9254L10.7464 15.9254C10.7876 16.3376 10.4869 16.7051 10.0747 16.7463C9.66256 16.7875 9.29503 16.4868 9.25381 16.0747L8.75381 11.0747C8.7126 10.6625 9.01331 10.295 9.42546 10.2538Z" fill="#EF4D56"></path> <path d="M14.5747 10.2538C14.9869 10.295 15.2876 10.6625 15.2464 11.0747L14.7464 16.0747C14.7052 16.4868 14.3376 16.7875 13.9255 16.7463C13.5133 16.7051 13.2126 16.3376 13.2538 15.9254L13.7538 10.9254C13.795 10.5133 14.1626 10.2125 14.5747 10.2538Z" fill="#EF4D56"></path> </g></svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Integrations Section */}
                            <div className='sa-integrations-section'>
                                <h4 className='sa-integrations-title'>Integrations</h4>
                                <p className='sa-integrations-desc'>
                                    Integrate {selectedAccount.name} into your account to enhance your performance
                                </p>

                                <div className='sa-channels-list'>
                                    {['email', 'sms', 'whatsapp'].map(channel => {
                                        const config = INTEGRATION_CONFIG[channel];
                                        const linkedIntegrations = getLinkedIntegrationsByChannel(channel);
                                        const availableIntegrations = getAvailableIntegrationsByChannel(channel);
                                        const isPopoverOpen = connectPopover.open && connectPopover.channel === channel;
                                        const isExpanded = expandedChannels[channel];
                                        const hasLinkedIntegrations = linkedIntegrations.length > 0;

                                        // Get integrations not already linked to this sales account
                                        const linkedIds = linkedIntegrations.map(i => i._id);
                                        const unlinkedIntegrations = availableIntegrations.filter(i => !linkedIds.includes(i._id))
                                            .map(i => {
                                                // Check if linked to another sales account
                                                const linkedAccount = i.salesAccountId
                                                    ? salesAccounts['data'].find(a => a._id === i.salesAccountId)
                                                    : null;
                                                return { ...i, linkedAccount };
                                            });

                                        return (
                                            <div key={channel} className='sa-channel-card'>
                                                {/* Channel Header */}
                                                <div className='sa-channel-header'>
                                                    <div className='sa-channel-info'>
                                                        <div className={`sa-channel-icon ${channel}`}>
                                                            {config.icon}
                                                        </div>
                                                        <div>
                                                            <h5 className='sa-channel-title'>
                                                                {config.title}
                                                                {hasLinkedIntegrations && (
                                                                    <span className='sa-channel-count'>
                                                                        ({linkedIntegrations.length} connected)
                                                                    </span>
                                                                )}
                                                            </h5>
                                                            <p className='sa-channel-description'>
                                                                {config.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className='sa-channel-actions'>
                                                        {/* Test domain setup button - visible only when no checkboxes are selected */}
                                                        {channel === 'email' && linkedIntegrations.length > 0 && checkedIntegrations.size === 0 && (
                                                        <IconButton title="Test domain setup" size="small" className="sa-domain-check-btn" disabled={domainCheckStatus === 'checking'} onClick={() => handleCheckDomainHealth(linkedIntegrations.map(i => i._id))}>                                                                {domainCheckStatus === 'checking'
                                                                    ? <i className="fa fa-spinner fa-spin" style={{ fontSize: 18 }} />
                                                                    : (
                                                                        <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <rect x="1" y="1" width="6.5" height="6.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                                            <rect x="10.5" y="1" width="6.5" height="6.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                                            <rect x="1" y="10.5" width="6.5" height="6.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                                            <rect x="10.5" y="10.5" width="6.5" height="6.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                                            <path d="M12 14l1.5 1.5 2.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        </svg>
                                                                    )}
                                                            </IconButton>
                                                        )}
                                                        {/* Edit settings icon - only for email channel when integrations are checked */}
                                                        {channel === 'email' && checkedIntegrations.size > 0 && (
                                                            <IconButton title="Edit selected accounts" size="small" onClick={() => { setSelectedIntegrationForSettings(null); setEmailSettingsOpen(true); }}>
                                                                <EditIcon fontSize="medium" />
                                                            </IconButton>
                                                        )}
                                                        {/* Connect button */}
                                                        <div
                                                            className='sa-connect-wrapper'
                                                            onMouseEnter={() => handleConnectMouseEnter(channel)}
                                                            onMouseLeave={handleConnectMouseLeave}
                                                        >
                                                            <button
                                                                onClick={() => handleOpenConnectPopover(channel)}
                                                                className='btn-style-border'
                                                            >
                                                                <i className='icon mr-2'>
                                                                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M4.56072 2.61629L3.26438 1.31995C2.72694 0.78251 1.85661 0.782511 1.31962 1.31984C0.782529 1.85693 0.782529 2.72735 1.31951 3.26434L3.91264 5.85747C4.44963 6.39447 5.32005 6.39447 5.85703 5.85747C6.03604 5.67846 6.32629 5.67846 6.5053 5.85747C6.68431 6.03648 6.68431 6.32672 6.5053 6.50573C5.61028 7.40076 4.1594 7.40076 3.26438 6.50573L0.671268 3.91263C-0.223756 3.0176 -0.223756 1.56673 0.671268 0.671701C1.56619 -0.223796 3.01717 -0.223796 3.91267 0.671701L5.209 1.96804C5.38801 2.14705 5.38801 2.4373 5.209 2.61631C5.03 2.79532 4.73973 2.7953 4.56072 2.61629Z" fill="currentColor" />
                                                                        <path d="M10.3289 10.3304C9.43383 11.2255 7.98249 11.2255 7.08746 10.3304L5.53214 8.77512C5.35313 8.59611 5.35313 8.30586 5.53214 8.12685C5.71115 7.94784 6.00139 7.94784 6.1804 8.12685L7.73573 9.68217C8.27271 10.2192 9.14358 10.2192 9.68059 9.68217C10.2176 9.14519 10.2176 8.27477 9.68059 7.73778L6.82848 4.88565C6.29147 4.34864 5.42107 4.34864 4.88409 4.88565C4.70508 5.06466 4.41483 5.06466 4.23582 4.88565C4.05681 4.70664 4.05681 4.41639 4.23582 4.23738C5.13084 3.34236 6.58172 3.34236 7.47674 4.23738L10.3289 7.0895C11.2239 7.98452 11.2239 9.43542 10.3289 10.3304Z" fill="currentColor" />
                                                                    </svg>
                                                                </i>
                                                                Connect
                                                            </button>

                                                            {/* Connect Popover */}
                                                            {isPopoverOpen && (
                                                                <div
                                                                    ref={popoverRef}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className='sa-popover'
                                                                >
                                                                    <div className='sa-popover-header'>
                                                                        <p>Select {config.name} Account</p>
                                                                    </div>

                                                                    <div className='sa-popover-list'>
                                                                        {unlinkedIntegrations.length > 0 ? (
                                                                            unlinkedIntegrations.map(integration => {
                                                                                const isLinkedElsewhere = !!integration.linkedAccount;
                                                                                return (
                                                                                    <div
                                                                                        key={integration._id}
                                                                                        onClick={() => !isLinkedElsewhere && handleLinkIntegration(integration._id)}
                                                                                        className={`sa-popover-item ${isLinkedElsewhere ? 'disabled' : ''}`}
                                                                                    >
                                                                                        <span>{getIntegrationLabel(integration)}</span>
                                                                                        {isLinkedElsewhere && (
                                                                                            <span className='sa-popover-item-linked'>
                                                                                                Linked to {integration.linkedAccount.name}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <div className='sa-popover-empty'>
                                                                                No available {config.name} accounts
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className='sa-popover-footer'>
                                                                        <div
                                                                            onClick={() => handleConnectNewAccount(channel)}
                                                                            className="sa-popover-add"
                                                                        >
                                                                            <i className='fa fa-plus'></i> Add new {config.name} account
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Expand/Collapse chevron - only show if has linked integrations */}
                                                        {hasLinkedIntegrations && (
                                                            <button
                                                                onClick={() => toggleChannel(channel)}
                                                                className='sa-toggle-btn'
                                                            >
                                                                <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Linked Integrations List - only show when expanded */}
                                                {hasLinkedIntegrations && isExpanded && (
                                                    <div className='sa-linked-list'>
                                                        {/* Domain health status banner */}
                                                        {channel === 'email' && domainCheckStatus && (() => {
                                                            const results = linkedIntegrations.map(i => domainHealthMap[i._id]).filter(Boolean);
                                                            const hasIssue = results.some(r => r['overall'] === 'issue');
                                                            const hasWarn = results.some(r => r['overall'] === 'warning');
                                                            const bannerClass = domainCheckStatus === 'checking' ? 'checking' : (hasIssue || hasWarn ? 'done has-issue' : 'done');
                                                            return (
                                                                <div className={`sa-domain-banner ${bannerClass}`}>
                                                                    {domainCheckStatus === 'checking' && <><i className="fa fa-spinner fa-spin" /> Checking all domains for MX, SPF, DKIM, and DMARC records</>}
                                                                    {domainCheckStatus === 'done' && (hasIssue || hasWarn) && (
                                                                        <><i className="fa fa-exclamation-triangle" /> Some domains have configuration issues <a href="https://docs.sellagent.ai/pages/email-dns-setup" target="_blank" rel="noopener noreferrer" className="sa-domain-banner-guide">Click here for guide</a></>
                                                                    )}
                                                                    {domainCheckStatus === 'done' && !hasIssue && !hasWarn && (
                                                                        <><i className="fa fa-check-circle" /> All accounts have MX, SPF, DKIM, and DMARC records set up! <a href="https://docs.sellagent.ai/pages/email-dns-setup" target="_blank" rel="noopener noreferrer" className="sa-domain-banner-guide">Click here for guide</a></>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                        {/* Header row for email channel */}
                                                        {channel === 'email' && (
                                                            <div className='sa-linked-header'>
                                                                <span className='sa-linked-header-email'>Email</span>
                                                                <span className='sa-linked-header-stat'>Emails Sent</span>
                                                                <span className='sa-linked-header-stat'>Warmup Emails</span>
                                                                <span className='sa-linked-header-stat'>Health</span>
                                                                <span className='sa-linked-header-actions'>Actions</span>
                                                            </div>
                                                        )}
                                                        {linkedIntegrations.map((integration) => {
                                                            const stats = channel === 'email' ? getEmailStats(integration._id) : null;
                                                            return (
                                                                <div key={integration._id} className={`sa-linked-item ${channel === 'email' ? 'with-stats' : ''}`}>
                                                                    <div className='sa-linked-info sa-linked-info-clickable' onClick={() => handleOpenEmailSettings(integration)}>
                                                                        {channel === 'email' && (
                                                                            <input
                                                                                type="checkbox"
                                                                                className='sa-integration-checkbox'
                                                                                checked={checkedIntegrations.has(integration._id)}
                                                                                onChange={e => { e.stopPropagation(); toggleIntegrationCheck(integration._id); }}
                                                                                onClick={e => e.stopPropagation()}
                                                                            />
                                                                        )}
                                                                        <i className='fa fa-check-circle'></i>
                                                                        <span>{getIntegrationLabel(integration)}</span>
                                                                        {channel === 'email' && (domainHealthMap[integration._id] || domainHealthLoading[integration._id]) && (
                                                                            <span
                                                                                className="sa-dh-info-icon"
                                                                                onMouseEnter={() => setHealthTooltipId(integration._id)}
                                                                                onMouseLeave={() => setHealthTooltipId(null)}
                                                                            >
                                                                                {domainHealthLoading[integration._id] ? <i className="fa fa-spinner fa-spin" style={{ fontSize: 11 }} /> : <InfoOutlinedIcon style={{ fontSize: 14 }} />}
                                                                                {healthTooltipId === integration._id && domainHealthMap[integration._id] && (() => {
                                                                                    const h = domainHealthMap[integration._id];
                                                                                    const RECORDS = [
                                                                                        { key: 'spf', label: 'SPF', desc: 'Sender Policy Framework' },
                                                                                        { key: 'dkim', label: 'DKIM', desc: 'DomainKeys Identified Mail' },
                                                                                        { key: 'dmarc', label: 'DMARC', desc: 'Domain-based Message Auth' },
                                                                                    ];
                                                                                    return (
                                                                                        <div className="sa-dh-tooltip">
                                                                                            <div className="sa-dh-tooltip-arrow" />
                                                                                            <div className="sa-dh-tooltip-header">
                                                                                                <span className="sa-dh-tooltip-domain">{h['domain']}</span>
                                                                                                <span className={`sa-dh-tooltip-overall ${h['overall']}`}>
                                                                                                    {h['overall'] === 'healthy' ? 'Healthy' : h['overall'] === 'warning' ? 'Warning' : 'Issues found'}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="sa-dh-tooltip-divider" />
                                                                                            {RECORDS.map(({ key, label, desc }) => {
                                                                                                const rec = h[key];
                                                                                                const passed = rec && rec['status'] === 'pass';
                                                                                                const warn = rec && rec['status'] === 'warn';
                                                                                                return (
                                                                                                    <div key={key} className="sa-dh-tooltip-row">
                                                                                                        <div className="sa-dh-tooltip-row-left">
                                                                                                            <span className={`sa-dh-tooltip-dot ${passed ? 'pass' : warn ? 'warn' : 'fail'}`} />
                                                                                                            <div>
                                                                                                                <div className="sa-dh-tooltip-label">{label}</div>
                                                                                                                <div className="sa-dh-tooltip-desc">{desc}</div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                        <span className={`sa-dh-tooltip-badge ${passed ? 'pass' : warn ? 'warn' : 'fail'}`}>
                                                                                                            {passed ? 'Passed' : warn ? 'Warning' : 'Not set up'}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {/* Show stats for email integrations */}
                                                                    {channel === 'email' && stats && (
                                                                        <>
                                                                            <div className='sa-linked-stat'>
                                                                                <span className='sa-stat-value'>{stats.emailsSent}</span>
                                                                            </div>
                                                                            <div className='sa-linked-stat'>
                                                                                <span className='sa-stat-value'>{stats.warmupEmails}</span>
                                                                            </div>
                                                                            <div className='sa-linked-stat'>
                                                                                <div className='sa-health-badge' style={{ backgroundColor: stats.healthScore !== null ? getHealthScoreColor(stats.healthScore) + '20' : '#6b728020', color: stats.healthScore !== null ? getHealthScoreColor(stats.healthScore) : '#6b7280' }}>
                                                                                    {stats.healthScore !== null ? `${stats.healthScore}%` : 'N/A'}
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                    <div className="sa-linked-actions">
                                                                        {channel === 'email' && (() => {
                                                                            const ws = emailSettings.data[integration._id]?.warmup;
                                                                            const isActive = ws?.enabled && !ws?.paused;
                                                                            const isPaused = ws?.enabled && ws?.paused;
                                                                            const flameClass = isActive ? 'active' : isPaused ? 'paused' : 'inactive';
                                                                            const tooltip = !ws?.enabled ? 'Warmup disabled' : ws?.paused ? 'Warmup paused' : 'Warmup enabled';
                                                                            return (
                                                                                <span className={`sa-warmup-flame ${flameClass}`} title={tooltip} onClick={() => handleToggleWarmup(integration)} style={{ cursor: 'pointer' }}>
                                                                                    <svg width="20" height="20" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                        <path d="M6 0C6 0 6.5 3 4.5 5C2.5 7 1 8.5 1 11C1 13.761 3.239 16 6 16C8.761 16 11 13.761 11 11C11 8 8.5 6.5 8 5C7.5 3.5 8 1.5 6 0Z" fill="currentColor"/>
                                                                                        <path d="M6 9C6 9 6.25 10.5 5.25 11.5C4.5 12.25 4 13 4 13.5C4 14.328 4.895 15 6 15C7.105 15 8 14.328 8 13.5C8 12.5 7 11.5 7 10.5C7 9.5 6 9 6 9Z" fill="white" opacity="0.4"/>
                                                                                    </svg>
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => handleOpenActionsMenu(e, integration)}
                                                                        >
                                                                            <MoreVertIcon fontSize="small" />
                                                                        </IconButton>
                                                                        <Menu
                                                                            anchorEl={actionsAnchorEl}
                                                                            open={openActionsMenu}
                                                                            onClose={handleCloseActionsMenu}
                                                                            PaperProps={{
                                                                                sx: {
                                                                                    borderRadius: 2,
                                                                                    minWidth: 160,
                                                                                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                                                                                    mt: 1
                                                                                }
                                                                            }}
                                                                        >
                                                                            {actionsIntegration && actionsIntegration?.toolId?.name === "Gmail" && canManage && (
                                                                                <MenuItem
                                                                                    onClick={() => {
                                                                                        handleOpenEmailSettings(actionsIntegration);
                                                                                        handleCloseActionsMenu();
                                                                                    }}
                                                                                >
                                                                                    <ListItemIcon>
                                                                                        <SettingsIcon fontSize="small" />
                                                                                    </ListItemIcon>
                                                                                    <ListItemText primary="Settings" />
                                                                                </MenuItem>
                                                                            )}

                                                                            {canManage && (
                                                                                <MenuItem
                                                                                    onClick={() => {
                                                                                        handleUnlinkIntegration(actionsIntegration?._id);
                                                                                        handleCloseActionsMenu();
                                                                                    }}
                                                                                    sx={{ color: "error.main" }}
                                                                                >
                                                                                    <ListItemIcon sx={{ color: "error.main" }}>
                                                                                        <CloseIcon fontSize="small" />
                                                                                    </ListItemIcon>
                                                                                    <ListItemText primary="Remove" />
                                                                                </MenuItem>
                                                                            )}
                                                                        </Menu>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className='sa-empty-state'>
                            <div className='sa-empty-icon'>
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#C5C7CD" />
                                </svg>
                            </div>
                            <h3>No Sales Accounts Yet</h3>
                            <p className='sa-empty-desc'>Create your first sales account to start sending personalized outreach</p>
                            {canManage && (
                                <button className='btn-style-new' onClick={handleOpenCreate}>
                                    <i className='fa fa-plus mr-2'></i> Add Sales Account
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Sales Account Modal */}
            <Modal isOpen={showCreateModal} toggle={handleCloseCreateModal} size='md' backdrop="static" className={`main-modal new-user-modal modal-dialog-centered workflow-modal`}>
                <ModalHeader toggle={handleCloseCreateModal}>
                    <h3>Create Sales Account </h3>
                </ModalHeader>
                <ModalBody>
                    <div className='content-area'>
                        <div className='form-area'>
                            <form onSubmit={handleCreateSubmit}>
                                <div className='group-form'>
                                    <label>New Sales Account</label>
                                    <TextField
                                        fullWidth
                                        hiddenLabel
                                        name="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Enter a name"
                                        required
                                        size="small"
                                    />
                                </div>

                                <div className='group-form'>
                                    <label>Select a role...</label>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={formData.role}
                                            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                                            label="Select a role..."
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {ROLE_OPTIONS.map(role => (
                                                <MenuItem key={role} value={role}>{role}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className='btns-box text-right'>
                                    <button type="button" className='btn-style-border' onClick={handleCloseCreateModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className='btn-style-new' disabled={salesAccountForm.loading}>
                                        {salesAccountForm.loading ? 'Creating...' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </ModalBody>
            </Modal>

            {/* Gmail Connect Modal */}
            <GmailConnectModal
                isOpen={gmailConnectOpen}
                toggle={() => setGmailConnectOpen(false)}
                salesAccountId={selectedAccount?._id}
                onDone={() => { dispatch(getIntegrations()); dispatch(getSalesAccounts()); }}
            />

            {/* Email Settings Modal */}
            <EmailSettingsModal isOpen={emailSettingsOpen} toggle={handleCloseEmailSettings} integration={selectedIntegrationForSettings} bulkIntegrationIds={!selectedIntegrationForSettings ? [...checkedIntegrations] : null} />

            {/* Signature Modal */}
            <SignatureModal
                isOpen={signatureModalOpen}
                toggle={handleCloseSignatureModal}
                signature=""
                firstName=""
                lastName=""
                salesAccounts={selectedAccount ? [selectedAccount] : salesAccounts.data}
                selectedSalesAccounts={[]}
                onSave={handleSaveSignature}
                saving={savingSignature}
            />
        </div>
    );
};

export default SalesAccounts;
